// Made-up data for demo mode: a Bengaluru brokerage with four telecallers and a week of calls. Everything is
// generated relative to "now" so overdue and due-today look right whenever the demo is opened. No real people.
import type { AiInstructions, CallType, ChecklistItem } from "./aiInstructions";
import type { Json } from "./database.types";
import { fromWallTime, localDate, wallParts } from "./format";
import type {
  Analysis,
  AnalysisOutput,
  Call,
  CallNote,
  Device,
  Digest,
  Disposition,
  Excluded,
  Holiday,
  Insight,
  Interest,
  Invitation,
  Lead,
  LeadEnquiry,
  LeadImport,
  Member,
  Org,
  PlatformAuditRow,
  PlatformOrg,
  QaCheck,
  QaLayer,
  QaStage,
  Recording,
  Segment,
  Source,
  Stage,
  Task,
  TaskType,
  Taxonomy,
  Transcript,
} from "./types";

/** A call's status as the server derives it from the call log (private.call_status_for). */
function callStatusFor(direction: string, durationS: number): string {
  if (direction === "missed") return "missed";
  if (direction === "rejected" || direction === "blocked") return "rejected";
  if ((direction === "incoming" || direction === "outgoing") && durationS > 0) return "connected";
  return direction === "outgoing" ? "not_answered" : "missed";
}

export const DEMO_TZ = "Asia/Kolkata";
const ORG_ID = "00000000-0000-4000-8000-000000000001";

export interface DemoDb {
  org: Org;
  aiInstructions: AiInstructions;
  taxonomies: Taxonomy[];
  members: Member[];
  leads: Lead[];
  calls: Call[];
  recordings: Recording[];
  transcripts: Transcript[];
  analyses: Analysis[];
  insights: Insight[];
  tasks: Task[];
  dispositions: Disposition[];
  invitations: Invitation[];
  devices: Device[];
  holidays: Holiday[];
  excluded: Excluded[];
  /** The company's sources, its imports and the enquiries they brought (D-097). */
  sources: Source[];
  leadImports: LeadImport[];
  enquiries: LeadEnquiry[];
  digests: Digest[];
  usageByDay: { day: string; minutes: number }[];
  platformOrgs: PlatformOrg[];
  platformAudit: PlatformAuditRow[];
  /** Owners' notes from running calls again (D-111). */
  callNotes: CallNote[];
  /** Call review (D-112): the company's checks and the reviews. */
  qaChecks: QaCheck[];
  qaReviews: DemoQaReview[];
}

/** One reviewer's review of one call at one stage (D-112). */
export interface DemoQaReview {
  recording_id: string;
  reviewer_member_id: string;
  stage: QaStage;
  answers: Record<string, boolean>;
  note: string | null;
  flagged: boolean;
  layer: QaLayer | null;
  prompt_version: string | null;
  updated_at: string;
}

/** The three checks every company starts with (D-112). */
export const QA_BUILT_IN_CHECKS: QaCheck[] = [
  { key: "speakers", label: "Did the AI tell the caller and the customer apart correctly?", built_in: true },
  { key: "transcript", label: "Was the transcript correct?", built_in: true },
  { key: "action", label: "Was the action correct?", built_in: true },
];

function rng(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let counter = 0;
const uuid = () => `00000000-0000-4000-8000-${String(++counter).padStart(12, "0")}`;

interface Line {
  who: "Caller" | "Customer";
  text: string;
}

interface Script {
  lines: Line[];
  interest: Interest;
  stage: Stage;
  summary: string;
  brief: string;
  objections: { category: string; detail: string; line: number }[];
  competitor?: { name: string; line: number };
  requests: string[];
  facts: Record<string, string>;
  next: {
    type: TaskType | "none";
    said: string | null;
    reason: string;
    inHours: number | "tomorrow_11" | "saturday_11";
  };
  evidence: number[];
}

const SCRIPTS: Script[] = [
  {
    lines: [
      {
        who: "Caller",
        text:
          "Namaste sir, main {caller} bol rahi hoon Sharma Realty se. Aapne Whitefield mein 2BHK ke liye enquiry ki thi.",
      },
      {
        who: "Customer",
        text: "Haan haan, yaad hai. Price kya chal raha hai abhi?",
      },
      {
        who: "Caller",
        text: "Sir 2BHK 72 lakh se start hai, possession December 2027 hai.",
      },
      {
        who: "Customer",
        text: "Thoda zyada hai budget se. Mera budget 60-65 tak hi hai.",
      },
      {
        who: "Caller",
        text: "Samajh sakti hoon sir. Ek option 64 lakh mein hai, thoda compact hai, 1050 square feet.",
      },
      {
        who: "Customer",
        text: "Accha, woh dekh sakte hain. Abhi main office mein hoon, baad mein call karna, shaam ko.",
      },
      {
        who: "Caller",
        text: "Bilkul sir, main shaam 6 baje call karti hoon aur floor plan WhatsApp kar deti hoon.",
      },
    ],
    interest: "warm",
    stage: "interested",
    summary:
      "Wants a 2BHK in Whitefield but the budget is ₹60–65 lakh against ₹72 lakh. Open to the 64-lakh compact unit; asked for a call back this evening.",
    brief: "2BHK Whitefield, budget 60–65L; call back this evening",
    objections: [{
      category: "budget",
      detail: "Budget ₹60–65 lakh, project starts at ₹72 lakh",
      line: 3,
    }],
    requests: ["Send the floor plan of the 64-lakh unit on WhatsApp"],
    facts: {
      configuration: "2BHK",
      location: "Whitefield",
      budget: "₹60–65 lakh",
    },
    next: {
      type: "call",
      said: "baad mein call karna, shaam ko",
      reason: "Customer asked for a call back in the evening",
      inHours: 5,
    },
    evidence: [5],
  },
  {
    lines: [
      {
        who: "Caller",
        text: "Hello sir, {caller} from Sharma Realty. Aapne Sarjapur wale project ka brochure dekha?",
      },
      {
        who: "Customer",
        text: "Haan dekha, 3BHK pasand aaya. Site visit kab ho sakta hai?",
      },
      {
        who: "Caller",
        text: "Sir is Saturday 11 baje kaisa rahega? Pick-up bhi arrange kar sakte hain.",
      },
      {
        who: "Customer",
        text: "Saturday theek hai. Wife ko bhi le aaunga, final decision unka hi hoga.",
      },
      {
        who: "Caller",
        text: "Perfect sir. Loan ke liye help chahiye toh bataiye, HDFC aur SBI dono ke saath tie-up hai.",
      },
      {
        who: "Customer",
        text: "Loan toh lena padega. Details WhatsApp kar dijiye.",
      },
    ],
    interest: "hot",
    stage: "site_visit",
    summary:
      "Liked the 3BHK in Sarjapur and booked a site visit for Saturday 11:00 with his wife, who will decide. Needs a home loan.",
    brief: "3BHK Sarjapur, site visit Saturday 11:00 with wife",
    objections: [
      {
        category: "authority",
        detail: "Wife will take the final decision",
        line: 3,
      },
      { category: "financing", detail: "Needs a home loan", line: 5 },
    ],
    requests: [
      "Send loan details on WhatsApp",
      "Arrange a pick-up for the site visit",
    ],
    facts: {
      configuration: "3BHK",
      location: "Sarjapur",
      decision_maker: "Wife",
    },
    next: {
      type: "site_visit",
      said: "Saturday theek hai",
      reason: "Site visit agreed for Saturday 11:00",
      inHours: "saturday_11",
    },
    evidence: [1, 3],
  },
  {
    lines: [
      {
        who: "Caller",
        text: "Good afternoon ma'am, {caller} from Sharma Realty. Aapne Hebbal ke flats ke baare mein poocha tha.",
      },
      {
        who: "Customer",
        text: "Actually humne already Prestige ka ek flat book kar liya hai, last week.",
      },
      {
        who: "Caller",
        text: "Oh congratulations ma'am. Agar investment ke liye kuch dekhna ho toh bataiye.",
      },
      { who: "Customer", text: "Nahi abhi toh nahi, thank you." },
    ],
    interest: "none",
    stage: "lost",
    summary: "Already booked a Prestige flat last week; not looking any more.",
    brief: "Bought elsewhere (Prestige); not interested",
    objections: [{
      category: "competitor",
      detail: "Booked a Prestige flat last week",
      line: 1,
    }],
    competitor: { name: "Prestige", line: 1 },
    requests: [],
    facts: { location: "Hebbal" },
    next: {
      type: "none",
      said: null,
      reason: "Customer bought elsewhere",
      inHours: 0,
    },
    evidence: [3],
  },
  {
    lines: [
      {
        who: "Caller",
        text: "Namaste ji, {caller} from Sharma Realty. Electronic City wale project ke liye call kiya tha.",
      },
      {
        who: "Customer",
        text: "Haan, lekin mujhe ready to move chahiye. 2027 bahut late hai, abhi rent de raha hoon.",
      },
      {
        who: "Caller",
        text: "Sir hamare paas do ready-to-move resale options bhi hain, 58 aur 61 lakh.",
      },
      {
        who: "Customer",
        text: "Accha? Unki details bhej dijiye, dekh ke batata hoon.",
      },
    ],
    interest: "warm",
    stage: "contacted",
    summary:
      "Needs a ready-to-move flat: possession in 2027 is too late while paying rent. Wants details of the two resale options (₹58 and ₹61 lakh).",
    brief: "Needs ready-to-move; send resale options",
    objections: [{
      category: "possession",
      detail: "Possession in 2027 is too late; paying rent now",
      line: 1,
    }],
    requests: ["Send details of the ready-to-move resale options"],
    facts: { location: "Electronic City", timeline: "Ready to move" },
    next: {
      type: "send_info",
      said: "details bhej dijiye",
      reason: "Send resale options",
      inHours: 2,
    },
    evidence: [3],
  },
  {
    lines: [
      {
        who: "Caller",
        text: "Hello sir, {caller} bol raha hoon Sharma Realty se.",
      },
      {
        who: "Customer",
        text: "Abhi drive kar raha hoon, kal subah call karo please.",
      },
      { who: "Caller", text: "Sure sir, kal subah 11 baje call karta hoon." },
    ],
    interest: "cold",
    stage: "contacted",
    summary: "Was driving; asked for a call tomorrow morning.",
    brief: "Busy driving; call tomorrow morning",
    objections: [{
      category: "timing",
      detail: "Busy, asked to call tomorrow morning",
      line: 1,
    }],
    requests: [],
    facts: {},
    next: {
      type: "call",
      said: "kal subah call karo",
      reason: "Customer asked for a call tomorrow morning",
      inHours: "tomorrow_11",
    },
    evidence: [],
  },
  {
    lines: [
      {
        who: "Caller",
        text: "Good evening sir, {caller} from Sharma Realty. Yelahanka villa project ke liye aapka interest tha.",
      },
      {
        who: "Customer",
        text: "Haan, villa achha hai, par builder ka track record kaisa hai? Pichhle project mein delay hua tha na?",
      },
      {
        who: "Caller",
        text: "Sir pichhla project 4 mahine late tha, par RERA registered hai aur is baar phase-wise delivery hai.",
      },
      {
        who: "Customer",
        text: "Hmm. RERA number aur approval documents bhej do, CA ko dikhana hai.",
      },
      { who: "Caller", text: "Zaroor sir, aaj hi email kar deta hoon." },
    ],
    interest: "warm",
    stage: "negotiating",
    summary:
      "Likes the Yelahanka villa but worries about the builder's past delay. Wants the RERA number and approvals for his CA.",
    brief: "Villa Yelahanka; wants RERA and approvals",
    objections: [
      {
        category: "builder_trust",
        detail: "Previous project was delayed",
        line: 1,
      },
      {
        category: "legal",
        detail: "Wants RERA number and approval documents for his CA",
        line: 3,
      },
    ],
    requests: ["Email the RERA number and approval documents"],
    facts: { configuration: "Villa", location: "Yelahanka" },
    next: {
      type: "email",
      said: "documents bhej do",
      reason: "Send RERA and approvals by email",
      inHours: 3,
    },
    evidence: [3],
  },
];

const FIRST = [
  "Rohit",
  "Sunita",
  "Vikram",
  "Pooja",
  "Amit",
  "Farhan",
  "Deepa",
  "Sanjay",
  "Meera",
  "Arjun",
  "Kiran",
  "Nisha",
  "Rajesh",
  "Anjali",
  "Suresh",
  "Lakshmi",
  "Manoj",
  "Shweta",
  "Abdul",
  "Kavya",
  "Gaurav",
  "Divya",
  "Harish",
  "Tanvi",
  "Prakash",
  "Ritu",
  "Naveen",
  "Swati",
  "Ashok",
  "Bhavna",
  "Venkat",
  "Zoya",
  "Mohan",
  "Isha",
  "Ramesh",
  "Farah",
];
const LAST = [
  "Mehta",
  "Iyer",
  "Singh",
  "Desai",
  "Patel",
  "Ali",
  "Menon",
  "Kulkarni",
  "Joshi",
  "Reddy",
  "Shah",
  "Agarwal",
  "Nair",
  "Rao",
  "Pillai",
  "Gowda",
  "Hegde",
  "Bhat",
  "Khan",
  "Chopra",
];

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const isSunday = (day: string) => new Date(`${day}T00:00:00Z`).getUTCDay() === 0;
const wall = (day: string, minutes: number) =>
  fromWallTime(
    `${day}T${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`,
    DEMO_TZ,
  );

/** The latest moment inside working hours (Mon–Sat 10:00–19:00) at or before now: recent demo activity happens then. */
function workingAnchor(now: Date): Date {
  let day = localDate(now, DEMO_TZ);
  const p = wallParts(now, DEMO_TZ);
  const minutes = p.h * 60 + p.min;
  if (!isSunday(day) && minutes >= 10 * 60 + 30 && minutes < 19 * 60) {
    return now;
  }
  if (minutes < 10 * 60 + 30) day = addDays(day, -1);
  while (isSunday(day)) day = addDays(day, -1);
  return wall(day, 18 * 60 + 30);
}

/** Moves a time into working hours, as the real scheduler does (SCHED-02). */
function snapForward(d: Date, jitter: number): string {
  let day = localDate(d, DEMO_TZ);
  const p = wallParts(d, DEMO_TZ);
  let minutes = p.h * 60 + p.min;
  if (minutes < 10 * 60) minutes = 11 * 60 + jitter;
  else if (minutes >= 19 * 60) {
    day = addDays(day, 1);
    minutes = 11 * 60 + jitter;
  }
  while (isSunday(day)) {
    day = addDays(day, 1);
    minutes = 11 * 60 + jitter;
  }
  return wall(day, minutes).toISOString();
}

const PHONES: [string, string][] = [
  ["Xiaomi", "Redmi 13"],
  ["Samsung", "Galaxy M34"],
  ["Realme", "Narzo 70"],
  [
    "Vivo",
    "Y28",
  ],
];
const SCRIPT_ORDER = [0, 1, 0, 3, 4, 0, 5, 1, 3, 2, 0, 4, 1, 0, 3, 5, 0, 2];

/** The starting checklist for real estate, as the database seeds it (private.default_ai_checklist). */
export const DEMO_CHECKLIST: ChecklistItem[] = [
  { key: "introduced", label: "Introduced themselves and the company", applies_to: "all" },
  {
    key: "asked_requirement",
    label: "Asked what the customer needs: configuration, budget and location",
    applies_to: "cold",
  },
  { key: "proposed_next_step", label: "Proposed a clear next step, such as a site visit", applies_to: "all" },
  { key: "answered_concern", label: "Answered the customer's main concern", applies_to: "conversion" },
  { key: "asked_for_booking", label: "Asked for the booking or token amount", applies_to: "conversion" },
];

/** The categories and checklist software and IT services starts with (20261001122056_business_types.sql). */
export const DEMO_SOFTWARE_CATEGORIES: [string, string, string | null][] = [
  ["budget", "Price or budget", "Too expensive, over budget, wants a discount or a cheaper plan"],
  ["timing", "Not now", "Not the right time, buying later, waiting for budget or a project"],
  ["no_need", "No clear need", "Does not see the need, happy with how things are done today"],
  ["existing_vendor", "Existing vendor", "Already uses another product or vendor, or is tied to a contract"],
  ["competitor", "Comparing other options", "Evaluating other products or vendors, or building it in-house"],
  ["features", "Missing features", "It does not do something they need"],
  ["integration", "Integration or setup", "Fit with their systems, data migration, setup or training effort"],
  ["security", "Security or compliance", "Data security, privacy, compliance, where the data is kept"],
  ["trust", "Trust or references", "Wants references, case studies or proof that it works"],
  ["authority", "Someone else decides", "A manager, IT, finance or partners must agree"],
  ["terms", "Contract or payment terms", "Contract length, payment terms, lock-in, refunds"],
  ["other", "Other", "Anything that fits no category above"],
];

export const DEMO_SOFTWARE_CHECKLIST: ChecklistItem[] = [
  { key: "introduced", label: "Introduced themselves and the company", applies_to: "all" },
  { key: "asked_need", label: "Asked what the customer needs and how they work today", applies_to: "cold" },
  { key: "asked_decision", label: "Asked who decides and by when", applies_to: "all" },
  { key: "proposed_next_step", label: "Proposed a clear next step, such as a demo or a meeting", applies_to: "all" },
  { key: "answered_concern", label: "Answered the customer's main concern", applies_to: "conversion" },
  {
    key: "asked_for_commitment",
    label: "Asked for the order, a trial or the next commitment",
    applies_to: "conversion",
  },
];

/** The categories and checklist education and training starts with (20261001144518_education_business_type.sql). */
export const DEMO_EDUCATION_CATEGORIES: [string, string, string | null][] = [
  ["budget", "Fees or budget", "Course fee too high; wants a discount, instalments or a cheaper course"],
  ["timing", "Not now", "Not the right time: exams, a job, joining later, waiting for results"],
  [
    "schedule",
    "Batch timing",
    "Class timings, weekday or weekend batches, course length clashing with college or work",
  ],
  [
    "location",
    "Distance or online",
    "The institute is too far or travel is hard; wants online classes, or classroom instead",
  ],
  [
    "course_fit",
    "Course content or level",
    "Unsure the course fits their goal, level or syllabus; wants other software or another course",
  ],
  ["placement", "Placement or job", "Wants a job or placement guarantee, an internship, or salary outcomes"],
  ["certification", "Certificate", "Whether the certificate is recognised or valued by employers"],
  ["trust", "Trust or reviews", "Wants reviews, a demo class, past students' results or other proof"],
  ["competitor", "Other institutes", "Comparing other institutes, online platforms or free courses"],
  ["authority", "Parent or someone else decides", "Parents, family or an employer must agree or pay"],
  ["no_need", "No clear need", "Just exploring; not sure they need the course"],
  ["other", "Other", "Anything that fits no category above"],
];

export const DEMO_EDUCATION_CHECKLIST: ChecklistItem[] = [
  { key: "introduced", label: "Introduced themselves and the institute", applies_to: "all" },
  {
    key: "asked_goal",
    label: "Asked about the student's background and what they want to learn or achieve",
    applies_to: "cold",
  },
  {
    key: "suggested_course",
    label: "Suggested a suitable course: what it covers, how long it takes and the fees",
    applies_to: "all",
  },
  {
    key: "proposed_next_step",
    label: "Proposed a clear next step, such as a demo class or a visit to the institute",
    applies_to: "all",
  },
  { key: "answered_concern", label: "Answered the student's main concern", applies_to: "conversion" },
  { key: "asked_to_enrol", label: "Asked them to enrol or pay the registration fee", applies_to: "conversion" },
];

/** The objection categories real estate starts with (supabase/taxonomies/real_estate.json). */
export const DEMO_CATEGORIES: [string, string, string | null][] = [
  ["budget", "Price or budget", "Price too high, over budget, wants a discount or a cheaper unit"],
  ["location", "Location", "Area, distance to work or school, connectivity, neighbourhood"],
  ["possession", "Possession date", "Under construction, possession too far away, fears of delay"],
  ["financing", "Loan or payment", "Loan eligibility, EMI, down payment, payment plan"],
  ["builder_trust", "Builder trust", "Builder reputation, RERA status, past delivery record"],
  ["legal", "Documents and approvals", "Title, approvals, registration, paperwork"],
  ["layout", "Size or layout", "Carpet area, floor, facing, vastu, number of rooms"],
  ["amenities", "Amenities or charges", "Missing amenities, maintenance charges, parking"],
  ["competitor", "Comparing other options", "Another project, another broker, resale property"],
  ["timing", "Not ready yet", "Just exploring, buying later, waiting for an event"],
  ["authority", "Someone else decides", "Spouse, parents or partner must agree or visit first"],
  ["other", "Other", "Anything that fits no category above"],
];

/** Words in a caller's line that show a checklist item was done, for the demo's made-up answers. */
const DEMO_EVIDENCE: Record<string, RegExp> = {
  asked_requirement: /bhk|budget|location|kahan|kitne|requirement|chahiye/i,
  proposed_next_step: /visit|baje|kal |tomorrow|saturday|sunday|whatsapp|bhej/i,
  asked_for_booking: /token|booking|book /i,
};

/**
 * How the demo caller did on the checklist items for their call type. An item is done when one of the caller's lines
 * shows it (the greeting always does; a concern is answered in the line after it) and a seeded coin agrees, so the
 * overview shows a spread; otherwise it was missed. A done item quotes that line.
 */
function demoChecklist(
  items: ChecklistItem[],
  callType: CallType | null,
  script: Script,
  segments: Segment[],
  tick: () => number,
): NonNullable<AnalysisOutput["checklist"]> {
  const isCaller = (i: number) => segments[i]?.speaker === "Caller";
  const evidence = (key: string): number | null => {
    if (key === "introduced") return isCaller(0) ? 0 : null;
    if (key === "answered_concern") {
      const after = (script.objections[0]?.line ?? -2) + 1;
      return isCaller(after) ? after : null;
    }
    const words = DEMO_EVIDENCE[key];
    const i = segments.findIndex((s, i) => isCaller(i) && words?.test(s.text));
    return i < 0 ? null : i;
  };
  return items.filter((i) => i.applies_to === "all" || i.applies_to === callType).map((item) => {
    if (item.key === "answered_concern" && script.objections.length === 0) {
      return { key: item.key, label: item.label, result: "na" as const, quote: null, t: null };
    }
    const line = evidence(item.key);
    if (line === null || (item.key !== "introduced" && tick() < 0.2)) {
      return { key: item.key, label: item.label, result: "no" as const, quote: null, t: null };
    }
    return {
      key: item.key,
      label: item.label,
      result: "yes" as const,
      quote: segments[line].text,
      t: segments[line].start,
    };
  });
}

export function buildDemo(now: Date = new Date()): DemoDb {
  counter = 0;
  const rand = rng(20261001);
  const pick = <T>(xs: T[]) => xs[Math.floor(rand() * xs.length)];
  const today = localDate(now, DEMO_TZ);
  const at = (dayOffset: number, hh: number, mm = 0) =>
    fromWallTime(
      `${addDays(today, dayOffset)}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,
      DEMO_TZ,
    )
      .toISOString();
  const iso = now.toISOString();
  const anchor = workingAnchor(now);

  const org: Org = {
    id: ORG_ID,
    name: "Sharma Realty",
    timezone: DEMO_TZ,
    default_country: "IN",
    vertical: "real_estate",
    working_hours: {
      mon: [["10:00", "19:00"]],
      tue: [["10:00", "19:00"]],
      wed: [["10:00", "19:00"]],
      thu: [["10:00", "19:00"]],
      fri: [["10:00", "19:00"]],
      sat: [["10:00", "19:00"]],
      sun: [],
    },
    settings: {
      follow_up: { retry_minutes: 120 },
      digest: { time: "08:00", enabled: true },
    },
    status: "active",
    suspended_at: null,
    suspended_reason: null,
    ai_minutes_included: 3000,
    plan: "base",
    trial_ends_on: at(-26, 0).slice(0, 10),
    billing_exempt: false,
    created_at: at(-40, 10),
    reviewed_at: at(-40, 10),
    updated_at: at(-2, 10),
  };

  const member = (
    name: string,
    role: Member["role"],
    phone: string | null,
    email: string | null,
  ): Member => ({
    id: uuid(),
    org_id: ORG_ID,
    auth_user_id: uuid(),
    display_name: name,
    role,
    phone_e164: phone,
    email,
    status: "active",
    removed_at: null,
    notice_version: role === "caller" ? "2026-10-02.1" : null,
    notice_accepted_at: role === "caller" ? at(-30, 10) : null,
    call_type: null,
    ai_note: "",
    work_name: null,
    call_brief: true,
    qa_role: null,
    created_at: at(-40, 10),
    updated_at: at(-40, 10),
  });
  const owner = member(
    "Anil Sharma",
    "owner",
    "+919845000001",
    "anil@sharmarealty.example",
  );
  const manager = member(
    "Kavita Rao",
    "manager",
    "+919845000002",
    "kavita@sharmarealty.example",
  );
  const callers = [
    member("Priya Nair", "caller", "+919845000011", null),
    member("Rahul Verma", "caller", "+919845000012", null),
    member("Neha Gupta", "caller", "+919845000013", null),
    member("Imran Khan", "caller", "+919845000014", null),
  ];
  // Two cold callers, one on conversion calls, one who makes both (TEAM-10).
  const callTypes: (CallType | null)[] = ["cold", "cold", "conversion", null];
  callers.forEach((c, i) => (c.call_type = callTypes[i]));
  callers[0].ai_note = "Calls new 99acres and MagicBricks leads for Whitefield.";
  callers[2].ai_note = "Handles leads after their site visit, mostly families buying a first home.";
  const members = [owner, manager, ...callers];

  const aiInstructions: AiInstructions = {
    about: "Sharma Realty sells 2 and 3 BHK flats in two projects: Green Meadows in Whitefield (from 85 lakh, " +
      "possession December 2027) and Lakeview Residency in Hebbal (ready to move, from 1.2 crore). " +
      "Site visits daily 10 to 6. This month: no floor-rise charge on bookings.",
    vocabulary: "Green Meadows\nLakeview Residency\nWhitefield\nHebbal\nSarjapur Road",
    competitors: "Prestige\nBrigade\nSobha",
    interest_guide: "Hot: wants a site visit this week and the budget fits. Warm: interested but no visit yet.",
    cold_guide: "Most cold leads come from 99acres and MagicBricks. The goal is a site visit.",
    conversion_guide: "Mention the no floor-rise offer. A token of 1 lakh holds a flat for 15 days.",
    extra: "Note if the customer is an NRI.",
    checklist: DEMO_CHECKLIST,
    facts: [
      { key: "configuration", label: "Configuration", hint: "2 BHK, 3 BHK, villa or plot" },
      { key: "loan_needed", label: "Loan needed", hint: "Yes or no" },
    ],
    version: 3,
    updated_at: at(-2, 16),
    updated_by: owner.id,
  };
  const taxonomies: Taxonomy[] = DEMO_CATEGORIES.map(([category, label, description], i) => ({
    id: uuid(),
    org_id: ORG_ID,
    kind: "objection",
    category,
    label,
    description,
    active: true,
    sort: category === "other" ? 99 : i + 1,
    created_at: at(-40, 10),
  }));
  // A separate stream, so the checklist answers leave the rest of the demo as it was.
  const tick = rng(4242);

  const devices: Device[] = callers.map((c, i) => ({
    id: uuid(),
    org_id: ORG_ID,
    member_id: c.id,
    install_id: `demo-install-${i + 1}`,
    manufacturer: PHONES[i % PHONES.length][0],
    model: PHONES[i % PHONES.length][1],
    os_version: "14",
    app_version: "1.0.0",
    session_id: null,
    last_seen_at: new Date(anchor.getTime() - (i + 1) * 7 * 60_000)
      .toISOString(),
    last_sync_at: new Date(anchor.getTime() - (i + 1) * 7 * 60_000).toISOString(),
    // One phone shows what the Team page says when a phone has stopped sending recordings (DEV-03).
    sync_problem: i === 2
      ? "The recordings folder was not found, so recordings are not being sent. Choose it again in Setup."
      : null,
    files_waiting: i === 2 ? 0 : i,
    // What each phone allows (APP-26): the third has no recordings folder, the second has the after-call card off.
    permissions: {
      call_log: true,
      notifications: true,
      recordings: i !== 2,
      after_call: i !== 1,
      battery: true,
    },
    permissions_at: new Date(anchor.getTime() - (i + 1) * 7 * 60_000).toISOString(),
    revoked_at: null,
    revoked_by: null,
    created_at: at(-30, 10),
  }));

  const leads: Lead[] = [];
  const calls: Call[] = [];
  const recordings: Recording[] = [];
  const transcripts: Transcript[] = [];
  const analyses: Analysis[] = [];
  const insights: Insight[] = [];
  const tasks: Task[] = [];
  const dispositions: Disposition[] = [];

  for (let i = 0; i < 36; i++) {
    const caller = callers[i % callers.length];
    const device = devices[i % callers.length];
    const script = SCRIPTS[SCRIPT_ORDER[i % SCRIPT_ORDER.length]];
    const phone = `+9198${String(45100000 + i * 7919).slice(0, 8)}`;
    const name = `${FIRST[i % FIRST.length]} ${LAST[(i * 7) % LAST.length]}`;
    const lead: Lead = {
      id: uuid(),
      org_id: ORG_ID,
      phone_e164: phone,
      name,
      owner_member_id: caller.id,
      stage: script.stage,
      interest: script.interest,
      summary: script.summary,
      facts: script.facts as Json,
      source: pick([
        "call_log",
        "call_log",
        "import",
        "99acres",
        "MagicBricks",
      ]),
      first_contact_at: null,
      last_contact_at: null,
      last_enquiry_at: null,
      source_id: null,
      campaign: null,
      created_at: at(-10, 10),
      updated_at: iso,
    };

    // One to three calls over the last week; the last one connected and recorded.
    const n = 1 + Math.floor(rand() * 3);
    let lastConnected: { call: Call; dayOffset: number } | null = null;
    for (let k = 0; k < n; k++) {
      const dayOffset = -Math.floor(rand() * 6) - (k === n - 1 ? 0 : 1);
      const hour = 10 + Math.floor(rand() * 8);
      const minute = Math.floor(rand() * 60);
      let started = at(dayOffset, hour, minute);
      if (new Date(started) > anchor) {
        started = new Date(anchor.getTime() - (60 + i * 11) * 60_000)
          .toISOString();
      }
      const connected = k === n - 1 || rand() > 0.4;
      const direction: Call["direction"] = connected
        ? (rand() > 0.25 ? "outgoing" : "incoming")
        : pick(["missed", "outgoing"]);
      const call: Call = {
        id: uuid(),
        org_id: ORG_ID,
        member_id: caller.id,
        device_id: device.id,
        lead_id: lead.id,
        phone_e164: phone,
        raw_number: phone.slice(3),
        contact_name: rand() > 0.5 ? name : null,
        direction,
        started_at: started,
        duration_s: connected && direction !== "missed" ? 60 + Math.floor(rand() * 240) : 0,
        sim: "1",
        created_at: started,
        call_status: null,
        outcome: null,
        disposition: null,
      };
      call.call_status = callStatusFor(call.direction, call.duration_s);
      calls.push(call);
      if (call.duration_s > 0) lastConnected = { call, dayOffset };
      lead.first_contact_at = !lead.first_contact_at || started < lead.first_contact_at
        ? started
        : lead.first_contact_at;
      lead.last_contact_at = !lead.last_contact_at || started > lead.last_contact_at ? started : lead.last_contact_at;
    }
    leads.push(lead);
    if (!lastConnected) continue;

    // The recording, transcript and analysis of the last connected call.
    const { call } = lastConnected;
    const rec: Recording = {
      id: uuid(),
      org_id: ORG_ID,
      member_id: caller.id,
      device_id: device.id,
      call_id: call.id,
      lead_id: lead.id,
      storage_path: `${ORG_ID}/${caller.id}/demo-${i}.m4a`,
      sha256: `demo${String(i).padStart(60, "0")}`,
      file_name: `Call recording ${name}_${call.started_at.slice(0, 10).replace(/-/g, "")}.m4a`,
      mime_type: "audio/mp4",
      size_bytes: call.duration_s * 16000,
      duration_s: call.duration_s,
      recorded_at: call.started_at,
      phone_hint: phone,
      match_confidence: 0.95,
      match_method: "file_name+time",
      status: "done",
      attempts: 1,
      audio_deleted_at: null,
      erase_requested_at: null,
      error: null,
      error_kind: null,
      processed_at: new Date(new Date(call.started_at).getTime() + 90_000)
        .toISOString(),
      created_at: new Date(new Date(call.started_at).getTime() + 40_000)
        .toISOString(),
      updated_at: iso,
    };
    recordings.push(rec);

    const per = Math.max(
      6,
      Math.floor(call.duration_s / (script.lines.length + 1)),
    );
    const segments: Segment[] = script.lines.map((l, idx) => ({
      speaker: l.who,
      start: idx * per + 1,
      end: idx * per + per - 1,
      text: l.text.replace("{caller}", caller.display_name.split(" ")[0]),
    }));
    transcripts.push({
      id: uuid(),
      org_id: ORG_ID,
      recording_id: rec.id,
      model: "demo",
      language: "hi-en",
      text: segments.map((s) => `${s.speaker}: ${s.text}`).join("\n"),
      segments: segments as unknown as Json,
      audio_seconds: call.duration_s,
      created_at: rec.processed_at!,
    });

    const tOf = (line: number) => segments[line]?.start ?? null;
    const output: AnalysisOutput = {
      summary: script.summary,
      brief: script.brief,
      customer_name: name,
      call_outcome: "connected",
      interest: {
        level: script.interest,
        confidence: 0.8,
        evidence: script.evidence.map((l) => ({
          quote: segments[l].text,
          t: tOf(l),
        })),
      },
      objections: script.objections.map((o) => ({
        category: o.category,
        detail: o.detail,
        quote: segments[o.line].text,
        t: tOf(o.line),
      })),
      competitors_mentioned: script.competitor
        ? [{
          name: script.competitor.name,
          quote: segments[script.competitor.line].text,
          t: tOf(script.competitor.line),
        }]
        : [],
      customer_requests: script.requests,
      facts: script.facts,
      next_action: {
        type: script.next.type,
        customer_said: script.next.said,
        reason: script.next.reason,
      },
      checklist: demoChecklist(aiInstructions.checklist, caller.call_type as CallType | null, script, segments, tick),
    };
    const analysis: Analysis = {
      id: uuid(),
      org_id: ORG_ID,
      recording_id: rec.id,
      lead_id: lead.id,
      prompt_version: "analysis-v2",
      model: "gemini-3.5-flash-lite",
      output: output as unknown as Json,
      summary: script.summary,
      brief: script.brief,
      interest: script.interest,
      call_type: caller.call_type,
      instructions_version: aiInstructions.version,
      created_at: rec.processed_at!,
    };
    analyses.push(analysis);
    for (const o of output.objections ?? []) {
      insights.push({
        id: uuid(),
        org_id: ORG_ID,
        analysis_id: analysis.id,
        recording_id: rec.id,
        lead_id: lead.id,
        member_id: caller.id,
        kind: "objection",
        category: o.category,
        detail: o.detail,
        quote: o.quote,
        t: o.t,
        result: null,
        created_at: analysis.created_at,
      });
    }
    for (const c of output.competitors_mentioned ?? []) {
      insights.push({
        id: uuid(),
        org_id: ORG_ID,
        analysis_id: analysis.id,
        recording_id: rec.id,
        lead_id: lead.id,
        member_id: caller.id,
        kind: "competitor",
        category: null,
        detail: c.name,
        quote: c.quote,
        t: c.t,
        result: null,
        created_at: analysis.created_at,
      });
    }
    for (const r of script.requests) {
      insights.push({
        id: uuid(),
        org_id: ORG_ID,
        analysis_id: analysis.id,
        recording_id: rec.id,
        lead_id: lead.id,
        member_id: caller.id,
        kind: "request",
        category: null,
        detail: r,
        quote: null,
        t: null,
        result: null,
        created_at: analysis.created_at,
      });
    }
    // Checklist items that applied: done or missed (PIPE-13).
    for (const k of output.checklist ?? []) {
      if (k.result === "na") continue;
      insights.push({
        id: uuid(),
        org_id: ORG_ID,
        analysis_id: analysis.id,
        recording_id: rec.id,
        lead_id: lead.id,
        member_id: caller.id,
        kind: "checklist",
        category: k.key,
        detail: k.label,
        quote: k.quote,
        t: k.t,
        result: k.result,
        created_at: analysis.created_at,
      });
    }

    // The open follow-up: spread across overdue, today, tomorrow and later.
    if (script.next.type !== "none") {
      const slot = i % 6;
      const jitter = (i % 4) * 15;
      const due = slot === 0
        ? new Date(anchor.getTime() - (40 + i * 3) * 60_000).toISOString()
        : slot === 1
        ? new Date(anchor.getTime() - (3 + (i % 4)) * 60 * 60_000).toISOString()
        : script.next.inHours === "tomorrow_11"
        ? snapForward(
          wall(addDays(localDate(anchor, DEMO_TZ), 1), 11 * 60),
          jitter,
        )
        : script.next.inHours === "saturday_11"
        ? at(
          ((6 - new Date(`${today}T00:00:00Z`).getUTCDay() + 7) % 7) || 7,
          11,
        )
        : snapForward(
          new Date(
            Math.max(
              now.getTime(),
              anchor.getTime() + (script.next.inHours as number) * 3600_000,
            ) + i * 60_000,
          ),
          jitter,
        );
      tasks.push({
        id: uuid(),
        org_id: ORG_ID,
        lead_id: lead.id,
        member_id: caller.id,
        type: script.next.type,
        due_at: due,
        reason: script.next.reason,
        brief: script.brief,
        source: "ai",
        source_rule: null,
        source_analysis_id: analysis.id,
        status: "open",
        snoozed_count: 0,
        completed_at: null,
        completed_by_call_id: null,
        created_by_member_id: null,
        created_at: analysis.created_at,
        updated_at: iso,
      });
    }
    if (i % 5 === 0) {
      dispositions.push({
        id: uuid(),
        org_id: ORG_ID,
        member_id: caller.id,
        recording_id: rec.id,
        analysis_id: analysis.id,
        lead_id: lead.id,
        task_id: null,
        action: "confirmed",
        corrected: {},
        task_type: null,
        task_due_at: null,
        created_at: analysis.created_at,
      });
    }
  }

  // Two missed calls nobody returned yet: rule-based tasks ten minutes out.
  for (const lead of leads.slice(30, 32)) {
    const caller = members.find((m) => m.id === lead.owner_member_id)!;
    const missedAt = new Date(anchor.getTime() - 25 * 60_000).toISOString();
    const call: Call = {
      id: uuid(),
      org_id: ORG_ID,
      member_id: caller.id,
      device_id: null,
      lead_id: lead.id,
      phone_e164: lead.phone_e164,
      raw_number: lead.phone_e164.slice(3),
      contact_name: lead.name,
      direction: "missed",
      started_at: missedAt,
      duration_s: 0,
      sim: "1",
      created_at: missedAt,
      call_status: "missed",
      outcome: null,
      disposition: null,
    };
    calls.push(call);
    lead.last_contact_at = missedAt;
    const existing = tasks.find((t) => t.lead_id === lead.id && t.status === "open");
    const rule = {
      type: "call" as const,
      due_at: new Date(anchor.getTime() - 15 * 60_000).toISOString(),
      reason: "Missed call; call back",
      source: "rule" as const,
      source_rule: `missed_call:${call.id}`,
    };
    if (existing) Object.assign(existing, rule);
    else {
      tasks.push({
        id: uuid(),
        org_id: ORG_ID,
        lead_id: lead.id,
        member_id: caller.id,
        brief: null,
        source_analysis_id: null,
        status: "open",
        snoozed_count: 0,
        completed_at: null,
        completed_by_call_id: null,
        created_by_member_id: null,
        created_at: missedAt,
        updated_at: iso,
        ...rule,
      });
    }
  }

  // A few follow-ups done in the last week, for the on-time numbers.
  for (const [idx, lead] of leads.slice(0, 8).entries()) {
    const due = at(-1 - (idx % 5), 12);
    tasks.push({
      id: uuid(),
      org_id: ORG_ID,
      lead_id: lead.id,
      member_id: lead.owner_member_id!,
      type: "call",
      due_at: due,
      reason: "Follow-up",
      brief: null,
      source: "ai",
      source_rule: null,
      source_analysis_id: null,
      status: "done",
      snoozed_count: 0,
      completed_at: new Date(
        new Date(due).getTime() + (idx % 3 === 0 ? 50 : 5) * 60_000,
      ).toISOString(),
      completed_by_call_id: null,
      created_by_member_id: null,
      created_at: at(-7, 10),
      updated_at: iso,
    });
  }

  const invitations: Invitation[] = [{
    id: uuid(),
    org_id: ORG_ID,
    display_name: "Sneha Pillai",
    role: "caller",
    phone_e164: "+919845000015",
    email: null,
    invited_by: owner.id,
    expires_at: new Date(now.getTime() + 6 * 86_400_000).toISOString(),
    accepted_at: null,
    accepted_member_id: null,
    revoked_at: null,
    created_at: at(-1, 16),
  }];

  const holidays: Holiday[] = [
    {
      id: uuid(),
      org_id: ORG_ID,
      day: "2026-10-20",
      name: "Diwali",
      created_at: at(-20, 10),
    },
    {
      id: uuid(),
      org_id: ORG_ID,
      day: "2026-11-01",
      name: "Karnataka Rajyotsava",
      created_at: at(-20, 10),
    },
    {
      id: uuid(),
      org_id: ORG_ID,
      day: "2026-12-25",
      name: "Christmas",
      created_at: at(-20, 10),
    },
  ];
  const sources: Source[] = ["Call", "Facebook ad", "Google ad", "Website", "Walk-in", "Referral", "Import"].map(
    (name, position) => ({
      id: uuid(),
      org_id: ORG_ID,
      name,
      type:
        ({ Call: "call", "Facebook ad": "ad", "Google ad": "ad", Website: "website", "Walk-in": "walk_in" } as Record<
          string,
          string
        >)[name] ?? (name === "Referral" ? "referral" : "import"),
      active: true,
      position,
      created_at: at(-30, 10),
      updated_at: at(-30, 10),
    }),
  );
  const excluded: Excluded[] = [
    {
      id: uuid(),
      org_id: ORG_ID,
      phone_e164: "+919845099999",
      reason: "Family",
      added_by: owner.id,
      created_at: at(-30, 10),
    },
    {
      id: uuid(),
      org_id: ORG_ID,
      phone_e164: "+918022220000",
      reason: "Office landline",
      added_by: owner.id,
      created_at: at(-30, 10),
    },
  ];

  const usageByDay: { day: string; minutes: number }[] = [];
  const monthStart = `${today.slice(0, 8)}01`;
  for (let d = monthStart; d <= today; d = addDays(d, 1)) {
    const dow = new Date(`${d}T00:00:00Z`).getUTCDay();
    usageByDay.push({
      day: d,
      minutes: dow === 0 ? 0 : 60 + Math.floor(rand() * 90),
    });
  }

  const digests: Digest[] = [0, 1, 2, 3, 4, 5, 6].map((back) => {
    const day = addDays(today, -back);
    const text = [
      `Voxowa for Sharma Realty, ${new Date(`${day}T00:00:00Z`).toUTCString().slice(0, 11)}`,
      "",
      `Yesterday: ${38 + back * 3} calls, ${27 + back} connected, ${3 + (back % 3)} hot leads. ${
        4 + (back % 4)
      } follow-ups overdue.`,
      "",
      "Hot leads:",
      ...leads.filter((l) => l.interest === "hot").slice(0, 3).map((l) => `- ${l.name}: ${SCRIPTS[1].brief}`),
      "",
      "Overdue follow-ups:",
      `- Rahul Verma: ${2 + (back % 3)}`,
      `- Neha Gupta: ${1 + (back % 2)}`,
      "",
      "Top objection this week: Price or budget (9)",
    ].join("\n");
    return {
      id: uuid(),
      org_id: ORG_ID,
      day,
      body: {} as Json,
      text,
      status: "sent",
      sent_at: at(-back, 8, 5),
      recipients: 2,
      email_claimed_at: at(-back, 8, 5),
      error: null,
      created_at: at(-back, 8, 1),
    };
  }).filter((d) => new Date(d.created_at) <= now);

  const platformOrgs: PlatformOrg[] = [
    {
      id: ORG_ID,
      name: "Sharma Realty",
      status: "active",
      suspended_reason: "",
      timezone: DEMO_TZ,
      default_country: "IN",
      created_at: org.created_at,
      owners: [{
        name: owner.display_name,
        email: owner.email,
        phone: owner.phone_e164,
      }] as unknown as Json,
      active_members: members.length,
      callers: callers.length,
      devices: devices.length,
      pending_invitations: 1,
      last_activity_at: iso,
      ai_minutes_included: 3000,
      ai_minutes_used: usageByDay.reduce((a, b) => a + b.minutes, 0),
      reviewed_at: org.created_at,
      held_recordings: 0,
      features: { coach: true, autopilot: true },
      vertical: "real_estate",
      member_limit: null,
      records_calls: true,
    },
    {
      id: uuid(),
      name: "Coastal Homes, Mangaluru",
      status: "active",
      suspended_reason: "",
      timezone: DEMO_TZ,
      default_country: "IN",
      created_at: at(-12, 11),
      owners: [{
        name: "Ravi Shetty",
        email: "ravi@coastalhomes.example",
        phone: null,
      }] as unknown as Json,
      active_members: 4,
      callers: 3,
      devices: 3,
      pending_invitations: 0,
      last_activity_at: at(0, 9, 40),
      ai_minutes_included: 3000,
      ai_minutes_used: 412,
      reviewed_at: at(-12, 11),
      held_recordings: 0,
      features: { coach: false, autopilot: true },
      vertical: "real_estate",
      member_limit: null,
      records_calls: true,
    },
    {
      id: uuid(),
      name: "Metro Brokers",
      status: "suspended",
      suspended_reason: "Invoice overdue",
      timezone: DEMO_TZ,
      default_country: "IN",
      created_at: at(-60, 11),
      owners: [{
        name: "S. Krishnan",
        email: "owner@metrobrokers.example",
        phone: null,
      }] as unknown as Json,
      active_members: 7,
      callers: 6,
      devices: 5,
      pending_invitations: 0,
      last_activity_at: at(-9, 18),
      ai_minutes_included: 3000,
      ai_minutes_used: 0,
      reviewed_at: at(-60, 11),
      held_recordings: 0,
      features: { coach: false, autopilot: false },
      vertical: "real_estate",
      member_limit: null,
      records_calls: false,
    },
    {
      id: uuid(),
      name: "Sunrise Properties, Pune",
      status: "active",
      suspended_reason: "",
      timezone: DEMO_TZ,
      default_country: "IN",
      created_at: at(-1, 16, 20),
      owners: [{
        name: "Meera Kulkarni",
        email: "meera@sunriseproperties.example",
        phone: null,
      }] as unknown as Json,
      active_members: 2,
      callers: 1,
      devices: 1,
      pending_invitations: 0,
      last_activity_at: at(0, 10, 5),
      ai_minutes_included: 3000,
      ai_minutes_used: 0,
      reviewed_at: null,
      held_recordings: 6,
      features: { coach: false, autopilot: true },
      vertical: "real_estate",
      member_limit: null,
      records_calls: true,
    },
  ];
  const platformAudit: PlatformAuditRow[] = [
    {
      at: at(-9, 18),
      org_id: platformOrgs[2].id,
      org_name: "Metro Brokers",
      actor_email: "admin@callscribe.example",
      action: "organization.suspended",
      detail: { reason: "Invoice overdue" },
    },
    {
      at: at(-12, 11),
      org_id: platformOrgs[1].id,
      org_name: "Coastal Homes, Mangaluru",
      actor_email: "admin@callscribe.example",
      action: "organization.created",
      detail: { name: "Coastal Homes, Mangaluru" },
    },
  ];

  return {
    org,
    aiInstructions,
    taxonomies,
    members,
    leads,
    calls,
    recordings,
    transcripts,
    callNotes: [],
    qaChecks: QA_BUILT_IN_CHECKS.map((c) => ({ ...c })),
    qaReviews: [],
    analyses,
    insights,
    tasks,
    dispositions,
    invitations,
    devices,
    holidays,
    excluded,
    sources,
    leadImports: [],
    enquiries: [],
    digests,
    usageByDay,
    platformOrgs,
    platformAudit,
  };
}

/** A silent WAV as long as the call, so the demo player can show seeking to a moment. */
export function demoAudioUrl(seconds: number): string {
  const rate = 8000;
  const n = Math.max(1, Math.round(seconds)) * rate;
  const buf = new ArrayBuffer(44 + n);
  const v = new DataView(buf);
  const w = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, "RIFF");
  v.setUint32(4, 36 + n, true);
  w(8, "WAVE");
  w(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate, true);
  v.setUint16(32, 1, true);
  v.setUint16(34, 8, true);
  w(36, "data");
  v.setUint32(40, n, true);
  for (let i = 0; i < n; i++) {
    v.setUint8(44 + i, 128);
  }
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
}
